import { useEffect, useState } from 'react';
import {
  getDocs, query, where, orderBy, limit as limitTo,
} from 'firebase/firestore';
import { col, COLLECTIONS } from './paths';
import { useAuth } from '../context/AuthContext';

// The delivery log's rows about one student and their family, for Message
// History's longer periods (#522).
//
// The outbox (`messages`) keeps a message ninety days after it is sent or
// failed; past that, only the sender's log (`messageDeliveries`, kept 400 days,
// one row per *attempt*) remembers it. Every period but Scheduled Queue asks
// for these rows as well, and the core folds them with the outbox
// (core/src/messages.rs `message_history_in`) — so this hook only fetches.
//
// **Three queries, never the collection.** The log grows with every send a
// studio makes and is not one of the windows the read store mirrors; a row
// carries `familyId`, `studentId` and `studentIds` denormalised precisely so
// it can be asked for this way (functions/delivery/deliver.js `deliveryRow`).
// A family-level message has the family's id and no student's; a message
// about a student who has no family has the student's; and a teacher's message
// names every student it reached in `studentIds` (#607), so only the third
// query (`array-contains`) finds a group email on the tabs of students it was
// not written from. A cancellation's `studentIds` match too, harmlessly: it
// also carries its family's id. `firestore.rules` refuses a list without
// a limit, and the bound is generous for one student over thirteen months.
// Merged by row id, since a row can match more than one.
//
// **Newest first, so the limit drops the oldest (#561).** An equality query
// with no `orderBy` comes back in document-id order, and attempt ids are
// random, so past the bound it would drop an arbitrary few. Each query orders by `finishedAt` descending —
// every row is written finished (functions/retention/store.js) — which needs
// the composite indexes `familyId` + `finishedAt`, `studentId` +
// `finishedAt` and `studentIds` (array-contains) + `finishedAt` in
// firestore.indexes.json. Those deploy on their own and take minutes to
// build, so while one is missing or still building the server refuses the
// ordered query with `failed-precondition`, and the read falls back to the
// unordered one, saying so in the console. The emulator and the offline cache
// need no index and never take that path.
//
// **`getDocs`, not a listener.** These are the older messages; the recent ones
// and everything still changing are the outbox's, which is live. Offline, the
// SDK answers from its cache, which is right for a log nobody on this device
// can write. Asked again whenever the student, the family or
// `enabled` changes — opening the tab, or choosing a longer period.
export const MESSAGE_LOG_LIMIT = 500;

export default function useMessageLog({ studentId = null, familyId = null, enabled = true } = {}) {
  const { user } = useAuth();
  const uid = user?.uid || null;
  // `key` is the ask an answer belongs to. Until the answer to *this* ask has
  // arrived the hook says `loading`, from the first render on (#629), so the
  // tab never draws "No messages in this period." before the effect has asked.
  const wanted = Boolean(uid && enabled && (studentId || familyId));
  const key = wanted ? `${uid}|${studentId || ''}|${familyId || ''}` : null;
  const [state, setState] = useState({ key: null, rows: [], loading: false, error: null });

  useEffect(() => {
    if (!wanted) {
      setState({ key: null, rows: [], loading: false, error: null });
      return undefined;
    }
    let live = true;
    setState((s) => ({ ...s, key, loading: true, error: null }));
    const log = col(uid, COLLECTIONS.messageDeliveries);
    const ask = (field, value, op = '==') => getDocs(query(
      log, where(field, op, value), orderBy('finishedAt', 'desc'), limitTo(MESSAGE_LOG_LIMIT),
    )).then(undefined, (err) => {
      if (err?.code !== 'failed-precondition') throw err;
      // What it saw: the ordered read was refused as a failed precondition,
      // which for this query is the composite index being absent or still
      // building. The unordered read needs none; past the limit, what it
      // drops is arbitrary rather than the oldest.
      console.warn(
        `[messages] the delivery log's ${field} + finishedAt index is missing or still building; `
          + 'reading it unordered (#561):',
        err,
      );
      return getDocs(query(log, where(field, op, value), limitTo(MESSAGE_LOG_LIMIT)));
    });
    const asked = [
      ...(familyId ? [ask('familyId', familyId)] : []),
      ...(studentId ? [ask('studentId', studentId), ask('studentIds', studentId, 'array-contains')] : []),
    ];
    Promise.all(asked).then(
      (snaps) => {
        if (!live) return;
        const byId = new Map();
        for (const snap of snaps) {
          for (const d of snap.docs) byId.set(d.id, { id: d.id, ...d.data() });
        }
        setState({ key, rows: [...byId.values()], loading: false, error: null });
      },
      (err) => {
        if (!live) return;
        // What it saw: the log did not answer. Offline with nothing cached, a
        // refused read and a malformed query all land here, and the tab still
        // lists everything the outbox holds.
        console.error('[messages] the delivery log did not answer; listing the outbox only:', err);
        setState({ key, rows: [], loading: false, error: err });
      },
    );
    return () => { live = false; };
  }, [uid, studentId, familyId, enabled, wanted, key]);

  if (!wanted) return { rows: [], loading: false, error: null };
  if (state.key !== key) return { rows: [], loading: true, error: null };
  return { rows: state.rows, loading: state.loading, error: state.error };
}
